/**
 * CafeteriaModeTip — the per-value provenance mark beside a number that came
 * from cafeteria mode, or that Production Records already holds.
 *
 *   logged     an Info icon: "Logged in Cafeteria Mode: 38.0°F · Maria Lopez ·
 *              Sep 15, 10:42 AM". Beside a production-record input the
 *              cafeteria pre-filled (a receipt or service temperature, a
 *              Delivered count), and beside a reading on the Temperature logs
 *              page itself. Name and time are left out when unknown (a paired
 *              kiosk has no user; a count carries no stamp).
 *   submitted  a Lock icon: "Submitted in Production Records · Jane Doe ·
 *              Sep 12, 3:40 PM", plus "Cafeteria logged 36.5°F" when the
 *              cafeteria's own reading differs. Beside a locked cell on the
 *              Temperature logs page.
 *
 * One mark per value instead of a banner over the whole step (Abner, Sep 15).
 * The trigger is a real button so the text is reachable by keyboard and by
 * assistive tech (`aria-label` carries the full sentence). Radix needs a
 * `TooltipProvider` above every tooltip and THROWS without one, so the tip
 * carries its own — call sites stay safe wherever they render.
 */

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ordo/ui/tooltip'
import { Info, Lock } from 'lucide-react'

export type CafeteriaModeTipProps =
  | { kind: 'logged'; label: string; loggedAt?: string | null; loggedByName?: string | null }
  /**
   * `kitchen` — the CHEF's own reading, pre-filled into a school input
   * (PROD-1279 spreadsheet mode). Same Info affordance as `logged`, different
   * source sentence: the school may overwrite the number, so the tip has to say
   * whose it was before they do.
   */
  | { kind: 'kitchen'; label: string; loggedAt?: string | null; loggedByName?: string | null }
  /**
   * `confirmed` — the value came from an EARLIER receipt of this shipment
   * (PROD-1279 spreadsheet mode). The cell stays editable: re-confirming a
   * delivery is a supported amendment, and the read-back takes the latest
   * receipt. The tip is what says the number is already on the record.
   */
  | { kind: 'confirmed'; at: string | null; byName: string | null; dateLabel: string }
  /**
   * `source` — where a pre-filled NUMBER came from when it is not a reading
   * anybody logged (PROD-1279 unified input): `From the order: 60`,
   * `From the delivery plan: 58`, `Saved on this record: 58`. The label is
   * the whole sentence, rendered verbatim behind the Info affordance.
   */
  | { kind: 'source'; label: string }
  | { kind: 'submitted'; at: string | null; byName: string | null; cafeteriaLabel?: string | null }

/** "Sep 15, 10:42 AM" in the viewer's zone; an unparseable stamp is shown as-is. */
export function formatTipStamp(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) {
    return iso
  }
  // Date and time formatted apart: one `toLocaleString` call joins them with
  // a locale connector ("at") that differs between ICU builds.
  const day = parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const time = parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${day}, ${time}`
}

/** The tooltip sentence — pure, unit-tested. */
export function cafeteriaModeTipText(props: CafeteriaModeTipProps): string {
  const parts: string[] = []
  if (props.kind === 'logged' || props.kind === 'kitchen') {
    parts.push(
      props.kind === 'kitchen' ? `Logged by the kitchen: ${props.label}` : `Logged in Cafeteria Mode: ${props.label}`,
    )
    if (props.loggedByName) {
      parts.push(props.loggedByName)
    }
    if (props.loggedAt) {
      parts.push(formatTipStamp(props.loggedAt))
    }
  } else if (props.kind === 'confirmed') {
    parts.push(`Confirmed on ${props.at ? formatTipStamp(props.at) : props.dateLabel}`)
    if (props.byName) {
      parts.push(props.byName)
    }
  } else if (props.kind === 'source') {
    parts.push(props.label)
  } else {
    parts.push('Submitted in Production Records')
    if (props.byName) {
      parts.push(props.byName)
    }
    if (props.at) {
      parts.push(formatTipStamp(props.at))
    }
    if (props.cafeteriaLabel) {
      parts.push(`Cafeteria logged ${props.cafeteriaLabel}`)
    }
  }
  return parts.join(' · ')
}

export function CafeteriaModeTip(props: CafeteriaModeTipProps) {
  const text = cafeteriaModeTipText(props)
  const Icon = props.kind === 'submitted' ? Lock : Info
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={text}
            data-tip-kind={props.kind}
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Icon className="h-3.5 w-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs whitespace-normal">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
